import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const VerificationTypes = () => {
  const [verificationTypes, setVerificationTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    label: '',
    type: 'text',
    required: true,
    validation: {
      minLength: '1',
      maxLength: '50',
      pattern: '',
      minValue: '0',
      maxValue: '1000000',
      allowedFileTypes: [],
      maxFileSize: '10'
    },
  });

  useEffect(() => {
    fetchVerificationTypes();
  }, []);

  const fetchVerificationTypes = async () => {
    try {
      setLoading(true);
      const response = await api.get('admin/verification-types');
      setVerificationTypes(response.data);
    } catch (error) {
      console.error('Failed to fetch verification types:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    formData.validation.minLength = formData.validation.minLength || 0;
    formData.validation.maxLength = formData.validation.maxLength || 0;
    formData.validation.minValue  = formData.validation.minValue || 0;
    formData.validation.maxValue = formData.validation.maxValue || 0;
    formData.validation.pattern = formData.validation.pattern || '1234';
    formData.validation.maxFileSize = formData.validation.maxFileSize || 0;
    try {
      setLoading(true);
      if (editingType) {
        await api.put(`/admin/verification-types/${editingType._id}`, formData);
      } else {
        await api.post('/admin/verification-types', formData);
      }
      setShowAddModal(false);
      setEditingType(null);
      fetchVerificationTypes();
    } catch (error) {
      console.error('Failed to save verification type:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (type) => {
    setEditingType(type);
    setFormData({
      name: type.name,
      label: type.label,
      type: type.type,
      required: type.required,
      validation: type.validation,
    });
    setShowAddModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this verification type?')) {
      try {
        await api.delete(`/admin/verification-types/${id}`);
        fetchVerificationTypes();
      } catch (error) {
        console.error('Failed to delete verification type:', error);
      }
    }
  };

  const renderValidationFields = () => {
    switch (formData.type) {
      case 'text':
        return (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Min Length</label>
                <input
                  type="number"
                  value={formData.validation.minLength}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    validation: { ...prev.validation, minLength: e.target.value }
                  }))}
                  className="w-full border rounded-md px-3 py-2 bg-gray-100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Max Length</label>
                <input
                  type="number"
                  value={formData.validation.maxLength}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    validation: { ...prev.validation, maxLength: e.target.value }
                  }))}
                  className="w-full border rounded-md px-3 py-2 bg-gray-100"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Pattern</label>
              <select
                value={formData.validation.pattern}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  validation: { ...prev.validation, pattern: e.target.value }
                }))}
                className="w-full border rounded-md px-3 py-2 bg-gray-100"
              >
                <option value="">Select pattern</option>
                <option value="^[0-9]+$">Number only</option>
                <option value="^[a-zA-Z]+$">Text only</option>
                <option value="^[A-Za-z0-9]+$">Alphanumeric</option>
                <option value="^[a-zA-Z0-9]+$">Alphanumeric (with spaces)</option>
                <option value="^[a-zA-Z0-9]+$">Alphanumeric (with spaces and underscores)</option>
                <option value="^[a-zA-Z0-9]+$">Alphanumeric (with spaces and underscores)</option>
                <option value="^[a-zA-Z0-9]+$">Alphanumeric (with spaces and underscores)</option>
                <option value="^[a-zA-Z0-9]+$">Alphanumeric (with spaces and underscores)</option>
              </select>
            </div>
          </>
        );
      case 'number':
        return (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Min Value</label>
              <input
                type="number"
                value={formData.validation.minValue}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  validation: { ...prev.validation, minValue: e.target.value }
                }))}
                className="w-full border rounded-md px-3 py-2 bg-gray-100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Max Value</label>
              <input
                type="number"
                value={formData.validation.maxValue}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  validation: { ...prev.validation, maxValue: e.target.value }
                }))}
                className="w-full border rounded-md px-3 py-2 bg-gray-100"
              />
            </div>
          </div>
        );
      case 'file':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700">Allowed File Types</label>
              <select
                multiple
                value={formData.validation.allowedFileTypes}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  validation: {
                    ...prev.validation,
                    allowedFileTypes: Array.from(e.target.selectedOptions, option => option.value)
                  }
                }))}
                className="w-full border rounded-md px-3 py-2 bg-gray-100"
              >
                <option value="application/pdf">PDF</option>
                <option value="image/jpeg">JPEG</option>
                <option value="image/png">PNG</option>
                <option value="application/msword">DOC</option>
                <option value="application/vnd.openxmlformats-officedocument.wordprocessingml.document">DOCX</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Max File Size (MB)</label>
              <input
                type="number"
                value={formData.validation.maxFileSize}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  validation: { ...prev.validation, maxFileSize: e.target.value }
                }))}
                className="w-full border rounded-md px-3 py-2 bg-gray-100"
              />
            </div>
          </>
        );
      default:
        return null;
    }
  };

  const renderVerificationMethodFields = () => {
    return null;
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Verification Types</h1>
        <button
          onClick={() => {
            setEditingType(null);
            setFormData({
              name: '',
              label: '',
              type: 'text',
              required: true,
              validation: {
                minLength: '',
                maxLength: '',
                pattern: '',
                minValue: '',
                maxValue: '',
                allowedFileTypes: [],
                maxFileSize: ''
              },
            });
            setShowAddModal(true);
          }}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          Add Verification Type
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Label</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Required</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Verification</th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {verificationTypes.map((type) => (
                <tr key={type._id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{type.label}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{type.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{type.type}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{type.required ? 'Yes' : 'No'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{type.verificationMethod}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleEdit(type)}
                      className="text-indigo-600 hover:text-indigo-900 mr-4"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(type._id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showAddModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleSubmit} className="p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {editingType ? 'Edit Verification Type' : 'Add Verification Type'}
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Name</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full border rounded-md px-3 py-2 bg-gray-100"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Label</label>
                    <input
                      type="text"
                      value={formData.label}
                      onChange={(e) => setFormData(prev => ({ ...prev, label: e.target.value }))}
                      className="w-full border rounded-md px-3 py-2 bg-gray-100"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Type</label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                      className="w-full border rounded-md px-3 py-2 bg-gray-100"
                    >
                      <option value="text">Text</option>
                      <option value="number">Number</option>
                      <option value="file">File Upload</option>
                      <option value="otp-email">OTP By Email</option>
                    </select>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.required}
                      onChange={(e) => setFormData(prev => ({ ...prev, required: e.target.checked }))}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-900">Required</label>
                  </div>
                  {renderValidationFields()}
                </div>
                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    {loading ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VerificationTypes;